/**
 * 头像编辑器（公共组件，2026-10-11 从三处消费方抽出）
 *
 * 三处共用同一套 `.studio-*`/`cc-*` 编辑器结构（预览镜 + 类目页签 + 部件条 [+ 鼻/嘴微调滑杆]）：
 *   开局建角页（CharacterCreatePanel）＝基准流：全类目可点，cfg 攒到「确认创建」才落档；
 *   造型间（StylingStudioPanel）＝全类目只列已拥有件，cfg 攒到「完成造型」才落档（免费）；
 *   商业街部件店（FreeroamMap）＝全件列带价签角标，点已拥有＝上身，点未拥有＝草稿预览（购买才落档）。
 * 组件统一管：cfg 状态、类目切换、联动置灰（isOptionDisabled）、刘海/侧发联动（applyFrontSideRule）、
 * 切脸型的鼻/嘴归位（faceNudgeOf）、条内高亮就地挪（syncStripState）/联动顶槽时整条重建（changedSlotCount>1）。
 * 落档语义差异由消费方的 onChange 自己写（组件⛔ 碰 player.avatar）。
 */
import { AvatarConfig, applyFrontSideRule, faceNudgeOf } from '../avatar/config';
import { FaceCategory, PALETTE_TONES, isOptionDisabled, syncStripState, changedSlotCount } from '../avatar/categories';
import { Tone } from '../avatar/palettes';
import { renderAvatarSVG } from '../avatar/renderer';
import { buildStripThumb } from '../avatar/thumbs';
import { createBtn, createDiv, createSpan, showHint } from './ui';

/** 条内角标（商店价签/「已拥有」），挂在部件卡右下 */
export interface AvatarEditorBadge {
    cls: string;
    text: string;
}

export interface AvatarEditorOptions {
    categories: FaceCategory[];
    /** 预览镜容器（组件往里画整脸 SVG） */
    preview: HTMLElement;
    tabs: HTMLElement;
    strip: HTMLElement;
    /** 鼻/嘴上下微调滑杆容器（传入＝启用该行；开局建角页/造型间/蒂尔玛用） */
    nudgeRow?: HTMLElement;
    /** 过滤：返回 false 的件不进条（缺省全进；性别过滤恒做）。造型间的 owned-only 走这里 */
    include?: (cat: FaceCategory, index: number) => boolean;
    /** 条内角标；返回 undefined 不挂。商业街价签走这里 */
    badge?: (cat: FaceCategory, index: number) => AvatarEditorBadge | undefined;
    /** 换件/微调后回调（cfg 已就地更新）：写档、存盘、购买脚部刷新由消费方在这里做 */
    onChange?: (cfg: AvatarConfig, cause: 'pick' | 'nudge') => void;
}

export class AvatarEditor {
    cfg: AvatarConfig;
    activeCat: FaceCategory;
    /** 最近一次点中的件（购买脚部「被点的这件」判定用；初始 null＝还没点过） */
    lastPick: { cat: FaceCategory; index: number } | null = null;

    private nudgeInput: HTMLInputElement | null = null;

    constructor(private readonly opts: AvatarEditorOptions, cfg: AvatarConfig) {
        this.cfg = cfg;
        this.activeCat = opts.categories[0];
        if (opts.nudgeRow) this.buildNudge(opts.nudgeRow);
        // 鼠标拖拽横滑（触屏走原生 overflow 滑动）——三处消费页共用组件，一处生效
        this.bindDragScroll(opts.strip);
        this.bindDragScroll(opts.tabs);
    }

    /**
     * 条/页签的鼠标横滑（触屏走原生 overflow 滑动）——三处消费页共用组件，一处生效：
     * ① 按住拖动=横向滚动（CSS 只有 overflow-x:auto，鼠标拖默认是图片原生拖拽鬼影）；
     *    拖过 5px 即 setPointerCapture 收编后续事件 ⇒ 松手那一下⛔ 落成部件点击（误买/误换），
     *    正常点击（位移 ≤5px）不走捕获，click 照常落在部件上；
     * ② 竖滚轮转横滚：条还能往手势方向滚时劫持并消费，滚到头/无溢出放行给页面。
     */
    private bindDragScroll(el: HTMLElement): void {
        el.style.cursor = 'grab';
        let startX = 0;
        let startLeft = 0;
        let dragged = false;
        let activeId: number | null = null;
        el.addEventListener('pointerdown', e => {
            if (e.pointerType !== 'mouse' || e.button !== 0) return;
            startX = e.clientX;
            startLeft = el.scrollLeft;
            dragged = false;
            activeId = e.pointerId;
        });
        el.addEventListener('pointermove', e => {
            if (activeId !== e.pointerId) return;
            const dx = e.clientX - startX;
            if (!dragged && Math.abs(dx) > 5) {
                dragged = true;
                el.setPointerCapture(e.pointerId);
                el.style.cursor = 'grabbing';
            }
            if (dragged) el.scrollLeft = startLeft - dx;
        });
        const release = (e: PointerEvent) => {
            if (activeId !== e.pointerId) return;
            activeId = null;
            el.style.cursor = 'grab';
            if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
        };
        el.addEventListener('pointerup', release);
        el.addEventListener('pointercancel', release);
        el.addEventListener('click', e => {
            if (!dragged) return;
            dragged = false;
            e.preventDefault();
            e.stopPropagation();
        }, true);
        el.addEventListener('wheel', e => {
            if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;   // 触控板横向手势走原生
            if (el.scrollWidth <= el.clientWidth) return;           // 无溢出交给页面
            const max = el.scrollWidth - el.clientWidth;
            const next = Math.max(0, Math.min(max, el.scrollLeft + e.deltaY));
            if (next === el.scrollLeft) return;                     // 已到头，链给页面滚
            el.scrollLeft = next;
            e.preventDefault();
        }, { passive: false });
    }

    /** 被点且仍在当前类目的那件（切过页签后视同无选中，脚部状态槽回到留空） */
    getPicked(): { cat: FaceCategory; index: number } | null {
        return this.lastPick && this.lastPick.cat === this.activeCat ? this.lastPick : null;
    }

    /** 换一张脸（进面板/随机造型）：lastPick 一并作废 */
    setConfig(cfg: AvatarConfig): void {
        this.cfg = cfg;
        this.lastPick = null;
    }

    /** 整体重渲（预览 + 页签 + 部件条 + 微调行） */
    rerender(): void {
        this.renderPreview();
        this.renderTabs();
        this.renderStrip();
        this.renderNudge();
    }

    private renderPreview(): void {
        this.opts.preview.innerHTML = renderAvatarSVG(this.cfg);
    }

    private renderTabs(): void {
        this.opts.tabs.innerHTML = '';
        this.opts.categories.forEach(cat => {
            const tab = createBtn('cc-tab', [cat.label]);
            tab.classList.toggle('selected', cat === this.activeCat);
            tab.addEventListener('click', e => {
                e.stopPropagation();
                this.activeCat = cat;
                this.renderTabs();
                this.renderStrip();
                this.renderNudge();
            });
            this.opts.tabs.appendChild(tab);
        });
    }

    /** 当前类目选项条：部件 = 整脸小图（裸部件图当占位）；色板 = 圆点（Tone 双色渐变） */
    private renderStrip(): void {
        const cat = this.activeCat;
        const strip = this.opts.strip;
        strip.innerHTML = '';
        if (!cat) return;
        if (cat.parts) {
            cat.parts
                .map((p, i) => ({ p, i }))
                .filter(({ p, i }) =>
                    (!p.gender || p.gender === this.cfg.gender) && (this.opts.include?.(cat, i) ?? true))
                .forEach(({ p, i }) => {
                    const disabled = isOptionDisabled(this.cfg, cat, p);
                    const opt = createBtn('cc-opt cc-opt-face', []);
                    opt.dataset.i = String(i);
                    buildStripThumb(opt, p, { ...this.cfg, [cat.key]: i } as AvatarConfig);
                    opt.appendChild(createSpan('cc-opt-label', p.label));
                    const badge = this.opts.badge?.(cat, i);
                    if (badge) opt.appendChild(createSpan(badge.cls, badge.text));
                    opt.classList.toggle('selected', this.cfg[cat.key] === i && !disabled);
                    if (disabled) {
                        opt.classList.add('disabled');
                        opt.addEventListener('click', e => {
                            e.stopPropagation();
                            showHint('这个选择与当前发型联动冲突');
                        });
                    } else {
                        opt.addEventListener('click', e => {
                            e.stopPropagation();
                            this.pick(cat, i);
                        });
                    }
                    strip.appendChild(opt);
                });
        } else if (cat.palette) {
            const tones = PALETTE_TONES[cat.palette];
            const isTone = (t: Tone | string): t is Tone => typeof t === 'object';
            tones.forEach((t, i) => {
                if (!(this.opts.include?.(cat, i) ?? true)) return;
                const opt = createBtn('cc-opt cc-swatch-opt', []);
                const sw = createDiv('cc-swatch');
                sw.style.background = isTone(t)
                    ? `linear-gradient(135deg, ${t.main} 55%, ${t.shade} 55%)`
                    : t;
                opt.appendChild(sw);
                const badge = this.opts.badge?.(cat, i);
                if (badge) opt.appendChild(createSpan(badge.cls, badge.text));
                opt.classList.toggle('selected', this.cfg[cat.key] === i);
                opt.addEventListener('click', e => {
                    e.stopPropagation();
                    this.pick(cat, i);
                });
                strip.appendChild(opt);
            });
        }
    }

    /**
     * 点一枚件：cfg 就地更新（联动规则统一在此），⛔ 重建整条——条内每张整脸小图已把本槽候选各画一遍，
     * 只就地挪高亮；唯一例外＝联动顶到第二个槽（刘海选「无」把侧发顶成「无刘海」），整条的图才真过期。
     */
    private pick(cat: FaceCategory, i: number): void {
        const prev = this.cfg;
        const next = applyFrontSideRule({ ...prev, [cat.key]: i } as AvatarConfig);
        // 切脸型：鼻/嘴自动落到该脸型的默认位置（之后滑杆仍可手调）
        this.cfg = cat.key === 'face' ? { ...next, ...faceNudgeOf(i) } : next;
        this.lastPick = { cat, index: i };
        this.renderPreview();
        if (changedSlotCount(prev, this.cfg) > 1) this.renderStrip();
        else syncStripState(this.opts.strip, this.cfg, cat);
        this.opts.onChange?.(this.cfg, 'pick');
    }

    /** 鼻/嘴 Y 微调滑杆（−6~+6 档），挂在消费方给的容器里，仅 nudge 类目显示 */
    private buildNudge(row: HTMLElement): void {
        row.appendChild(createSpan('cc-nudge-label', '上下微调'));
        const input = document.createElement('input');
        input.type = 'range';
        input.className = 'cc-nudge';
        input.min = '-6';
        input.max = '6';
        input.step = '1';
        input.addEventListener('input', () => {
            const key = this.activeCat.nudge;
            if (!key) return;
            this.cfg = { ...this.cfg, [key]: Number(input.value) };
            // 条内整脸小图⛔ 跟着滑杆重画：用户 2026-10-04 拍板「不管」（捏脸工坊同此口径），⛔ 当 bug 立项
            this.renderPreview();
            this.opts.onChange?.(this.cfg, 'nudge');
        });
        row.appendChild(input);
        this.nudgeInput = input;
    }

    private renderNudge(): void {
        if (!this.nudgeInput || !this.opts.nudgeRow) return;
        const key = this.activeCat.nudge;
        this.opts.nudgeRow.style.display = key ? 'flex' : 'none';
        if (key) this.nudgeInput.value = String(this.cfg[key]);
    }
}
